import { useState } from 'react';

import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';

import {
    CANCEL_WITHDRAWAL_ERROR_CODES,
    getCancelWithdrawalErrorCode,
    useCancelWithdrawal,
} from './use-cancel-withdrawal';

/**
 * Drives the cancel-withdrawal confirmation sheet and maps a failed cancellation to the message
 * the screen shows in its place. `blockedMessage`, once set, replaces the cancel button.
 */
export const useCancelWithdrawalAction = (userWalletId: string) => {
    const t = useSocialWalletTranslation('transaction_details');
    const { showInfoSheet, showToast } = useAlerts();
    const { mutateAsync: cancelWithdrawal, isPending } = useCancelWithdrawal();

    const [blockedMessage, setBlockedMessage] = useState<string | null>(null);

    const handleConfirmCancel = async () => {
        try {
            await cancelWithdrawal(userWalletId);
        } catch (error) {
            const code = getCancelWithdrawalErrorCode(error);
            const message =
                code === CANCEL_WITHDRAWAL_ERROR_CODES.NO_LONGER_PENDING
                    ? t('cancel_withdrawal_processed')
                    : t('cancel_withdrawal_error');
            setBlockedMessage(message);
            showToast({ message, toastType: 'warning' });
        }
    };

    const handlePress = () => {
        showInfoSheet({
            title: t('cancel_withdrawal_title'),
            description: t('cancel_withdrawal_description'),
            buttonLabel: t('cancel_withdrawal_confirm'),
            handlePress: handleConfirmCancel,
            showCancel: true,
            primaryButtonTestId: 'txnDetails-cancelWithdrawalConfirm',
        });
    };

    return { handlePress, isPending, blockedMessage };
};
